import { Form } from '@epages/react-components'
import { createElement, useState } from 'react'
import Immutable from 'immutable'

import { formatHref } from '../../utils/link-sanitizer'
import { getTitle } from '../../utils/get-plugin-title'
import { imageColorTextSettingsDefaults } from '../../../../../utils/hooks/colorSettings'
import { trimData } from '../../utils/trim-data'
import { useTranslate } from '../../../../../utils/translate'
import { withImageData, withMergedImageData } from './ImageField'
import Button from '../../../../formComponents/Button'
import ButtonRow from '../../../../formComponents/ButtonRow'
import ImageSettingsComponent from '../components/ImageSettings'
import ImageTextColorSettings from '../components/ImageTextColorSettings'
import ImageTextSettings from '../components/ImageTextSettings'

function normalize(data) {
  return withMergedImageData(trimData(data).update('link', formatHref)).remove('imageData')
}

export function normalizeButtonState(data: ImmutableMap) {
  return !data.get('link') ? data.set('buttonenabled', false).set('opentab', false) : data
}

const tabs = {
  imageSettings: ImageSettingsComponent,
  imageTextSettings: ImageTextSettings,
  imageTextColorSettings: ImageTextColorSettings,
}

const textFieldsList = ['text', 'headline', 'buttontext']

export const getTextPersistanceInfo = (source: ImmutableMap) =>
  textFieldsList.reduce(
    (acc, name) =>
      acc.set(
        name,
        name !== 'buttontext' ? Boolean(source.get(name)) : Boolean(source.get(name) && source.get('buttonenabled')),
      ),
    Immutable.Map(),
  )

const imageTextSettingsDefaults = Immutable.Map({
  text: '',
  buttontext: '',
  headline: '',
  link: '',
  buttonenabled: false,
  opentab: false,
})

export default function ImageSettings({
  config,
  data,
  onDataChange,
  onSave,
  onCancel,
  renderLayout,
}: Readonly<WorkspacePluginSettingsProps>) {
  const [error, setError] = useState(null)
  const [activeTab, setActiveTab] = useState('imageSettings')

  const t = useTranslate('interface', 'components.imageElementComponent.settingsModal')

  const renderBody = () => {
    const props = {
      error,
      config,
      handleError: (error) => {
        setError(error)
      },
      imageInfo: {
        src: data.get('src'),
      },
      textPersistanceInfo: getTextPersistanceInfo(data),
    }

    return createElement(tabs[activeTab], props)
  }

  return (
    <Form
      name="pluginImageSettings"
      value={withImageData(normalizeButtonState(data))}
      prepare={(data) => withImageData(data.mergeDeep(imageTextSettingsDefaults, imageColorTextSettingsDefaults, data))}
      // not normalizing (trimming) because we cannot type spaces otherwise
      onChange={(data) => onDataChange(withMergedImageData(normalizeButtonState(data)))}
      onSubmit={onSave}
      normalize={normalize}
    >
      {renderLayout({
        title: getTitle({
          tab: activeTab,
          tScoped: t,
          isEditActive: data.get('src'),
        }),
        tabs: Object.keys(tabs).map((tab) => ({
          name: tab,
          title: getTitle({
            tab,
            tScoped: t,
            isEditActive: data.get('src'),
          }),
          active: activeTab === tab,
          onClick: (name) => {
            setActiveTab(name)
          },
        })),
        body: renderBody(),
        footer: (
          <ButtonRow>
            <Button
              type="button"
              variant="cancel"
              onClick={() => {
                setError(null)
                onCancel()
              }}
            >
              {t('cancelButton.label')}
            </Button>
            <Button>{t('saveButton.label')}</Button>
          </ButtonRow>
        ),
      })}
    </Form>
  )
}
